import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { ModalDialogParams } from "../../../modal/modals/modal.dialog.component";
import { Table } from "../../../s25-table/Table";
import { GenericTableButtonComponent } from "../../../s25-table/generics/generic.table.button.component";
import { EmbeddedConfigListItem, EmbeddedConfigService } from "../../../s25-embedding-config/embedded.config.service";
import { Bind } from "../../../../decorators/bind.decorator";
import { ModalService } from "../../../modal/modal.service";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { S25EmbeddingUtil } from "./s25.embedding.util";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Util } from "../../../../util/s25-util";
import { S25Const } from "../../../../util/s25-const";

@TypeManagerDecorator("s25-ng-embedding-list")
@Component({
    selector: "s25-ng-embedding-list",
    template: `
        <h2 class="c-margin-bottom--single">Embedding Settings</h2>
        <div *s25IfShow="mode === 'list'">
            <button class="aw-button aw-button--primary c-margin-bottom--single" (click)="onCreate()">
                Add Config
            </button>
            <s25-ng-table [dataSource]="tableConfig" [columnSortable]="true" [hasFilter]="true"></s25-ng-table>
        </div>
        @if (mode === "preview") {
            <h3>Previewing: {{ preview.configName }}</h3>
            <s25-ng-button [type]="'danger--outline'" (clicked)="setMode('list')">Back</s25-ng-button>
            <p class="c-margin-top--single ngBold">Embedding is loaded via:</p>
            <pre class="code" [innerHTML]="code"></pre>
            <div class="buttons">
                <s25-ng-button [type]="'primary'" (clicked)="copyCode()"> Copy Code </s25-ng-button>
                <a [href]="previewLink" target="_blank" class="aw-button aw-button--outline">Open in new tab</a>
            </div>

            <div id="{{ preview.targetId }}"></div>
        }
    `,
    styles: `
        .code {
            width: fit-content;
            color: #555;
            padding: 1rem;
            background: #f4f4f4;
            border: solid 1px #e1e1e1;
            font-size: 90%;
            line-height: 1.2em;
            font-family: var(--font--pre), monospace;
        }

        .buttons {
            padding-block: 1rem;
            display: flex;
            gap: 0.5rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EmbeddingListComponent implements OnInit {
    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;

    mode: "list" | "preview" = "list";
    tableConfig: Table.DataSource;
    preview: EmbeddedConfigListItem;
    instance = S25Util.instanceId;
    configs: Record<string, EmbeddedConfigListItem>;
    previewLink: string;
    code: string;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        this.tableConfig = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: [
                {
                    id: "name",
                    header: "Name",
                },
                {
                    id: "type",
                    header: "Type",
                },
                {
                    id: "target",
                    header: "Target ID",
                },
                GenericTableButtonComponent.Column("Preview", this.onPreview, "outline"),
                GenericTableButtonComponent.Column("Copy", this.onCopy, "outline"),
                GenericTableButtonComponent.Column("Edit", this.onEdit, "outline"),
                GenericTableButtonComponent.Column("Delete", this.onDelete, "danger--outline"),
            ],
        };

        await S25Util.loadScript("embedded25", S25EmbeddingUtil.clientScriptUrl());
    }

    @Bind
    async getRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        const configs = await EmbeddedConfigService.getConfigs();
        this.configs = Object.fromEntries(configs.map((c) => [c.configToken, c]));
        return {
            rows: configs.map(this.mapToRow),
        };
    }

    mapToRow(config: EmbeddedConfigListItem): Table.Row {
        return {
            id: config.configToken,
            name: String(config.configName),
            cells: {
                name: { text: config.configName },
                type: { text: S25EmbeddingUtil.types[config.embeddedType].name },
                target: { text: config.targetId },
            },
        };
    }

    onCreate() {
        ModalService.modal("edit-embedded-config", {
            mode: "create",
            title: `Create Embedded Config`,
            onSave: this.tableComponent.refresh,
        });
    }

    @Bind
    async onPreview(row: Table.Row) {
        this.mode = "preview";
        this.preview = this.configs[row.id];
        this.previewLink = `${S25Const.baseUrl}/embedded/preview?token=${this.preview.configToken}&target=${encodeURIComponent(this.preview.targetId)}&instance=${S25Const.instanceId}`;

        this.code = `&lt;script src="https://25live.collegenet.com/pro/embedding-client-code/embedded25.js"&gt;&lt;/script&gt;
&lt;div id="${this.preview.targetId}"&gt;&lt;/div&gt;
&lt;script type="text/javascript"&gt;
    embedded25.fromConfig.create("${this.instance}", "${this.preview.configToken}");
&lt;/script&gt;`;

        this.cd.detectChanges();
        window.embedded25?.fromConfig.create(this.instance, this.preview.configToken);
    }

    @Bind
    onCopy(row: Table.Row) {
        ModalService.modal("edit-embedded-config", {
            config: String(row.id),
            mode: "copy",
            title: `Copy Config: ${row.name}`,
            onSave: this.tableComponent.refresh,
        });
    }

    @Bind
    onEdit(row: Table.Row) {
        ModalService.modal("edit-embedded-config", {
            config: String(row.id),
            title: `Edit Config: ${row.name}`,
            onSave: this.tableComponent.refresh,
        });
    }

    @Bind
    async onDelete(row: Table.Row) {
        const dialogData = ModalService.dialogType(
            "Yes No",
            {
                message: "Are you sure you want to delete this embedding configuration?",
                title: "Confirm Deletion",
            },
            "No",
        );
        await ModalService.modal("dialog", dialogData);
        if (dialogData.answer !== ModalDialogParams.Answer.Yes) return;

        await EmbeddedConfigService.deleteConfig(row.id as string);
        return this.tableComponent.refresh();
    }

    setMode(mode: typeof this.mode) {
        this.mode = mode;
        window.embedded25?.clearEventListeners(); // Clear event listeners from preview
        this.cd.detectChanges();
    }

    copyCode() {
        S25Util.copyToClipboard(S25Util.unescapeXml(this.code));
    }
}
